import { inject, Injectable } from '@angular/core'
import type { HttpResponse } from '@angular/common/http'
import type { Observable } from 'rxjs'
import { map, Subject, tap } from 'rxjs'
import type { QueryParams } from '@awork/_shared/services/api-client/ApiClient'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { apiEndpoint } from '@awork/environments/environment'
import { FilterContributor } from '@awork/_shared/models/filter-contributor.model'
import { FilterTeamAccess } from '@awork/_shared/models/filter-team-access.model'
import { FilterSuggestion } from '@awork/_shared/models/filter-suggestion.model'
import { DraftFilterEntity, FilterEntity, type FilterDefinition } from '@awork/_shared/models/filters.model'
import { FilterEntityType } from '@awork/_shared/models/filters.types'
import type { AccessLevels } from '@awork/features/workspace/models/permissions.model'
import { EntityFilterQuery } from '@awork/_shared/state/entity-filter.query'
import { EntityFilterStore } from '@awork/_shared/state/entity-filter.store'
import { Task } from '@awork/features/task/models/task.model'
import { TaskStore } from '@awork/features/task/state/task.store'
import { Project } from '@awork/features/project/models/project.model'
import { ProjectStore } from '@awork/features/project/state/project.store'
import { User } from '@awork/features/user/models/user.model'
import { UserStore } from '@awork/features/user/state/user.store'
import { Company } from '@awork/features/company/models/company.model'
import { CompanyStore } from '@awork/features/company/state/company.store'
import { DraftFilterStore } from '@awork/_shared/state/draft-filter.store'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import { OnboardingTaskKey } from '@awork/features/onboarding/models/types'
import { OnboardingService } from '@awork/features/onboarding/services/onboarding.service'
import { take } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class EntityFilterService {
  private readonly apiClient = inject(ApiClient)
  private readonly entityFilterQuery = inject(EntityFilterQuery)
  private readonly entityFilterStore = inject(EntityFilterStore)
  private readonly draftFilterStore = inject(DraftFilterStore)
  private readonly signalService = inject(SignalService)
  private readonly taskStore = inject(TaskStore)
  private readonly projectStore = inject(ProjectStore)
  private readonly userStore = inject(UserStore)
  private readonly companyStore = inject(CompanyStore)
  private readonly trackingService = inject(TrackingService)
  private readonly onboardingService = inject(OnboardingService)
  private readonly baseUrl = `${apiEndpoint}`
  private readonly filterResultsPageSize = 5000

  private readonly draftUpdatedSubject = new Subject<DraftFilterEntity>()
  readonly draftUpdated$ = this.draftUpdatedSubject.asObservable()

  constructor() {
    this.registerSignal()
  }

  /**
   * Registers the filter signal to auto-sync the store via WebSocket
   */
  private registerSignal(): void {
    this.signalService.getSignal('filter', this.entityFilterStore)
  }

  /**
   * Preview a filter without save
   * @param {FilterEntityType} entityType
   * @param {FilterDefinition} filterDefinition
   * @returns {Observable<Task[] | Project[] | Company[] | User[]>}
   */
  applyFilter(
    entityType: FilterEntityType,
    filterDefinition: FilterDefinition
  ): Observable<Task[] | Project[] | Company[] | User[]> {
    return this.apiClient.post<Task[] | Project[] | Company[] | User[]>(
      `${this.baseUrl}/filters/${entityType}/evaluate`,
      { filterDefinition }
    )
  }

  /**
   * Preview a filter and return only the total amount of matching entities.
   * @param {FilterEntityType} entityType
   * @param {FilterDefinition} filterDefinition
   * @returns {Observable<number>}
   */
  applyFilterCount(entityType: FilterEntityType, filterDefinition: FilterDefinition): Observable<number> {
    const params = ApiClient.getQueryParams({ count: true })

    return this.apiClient
      .post<HttpResponse<unknown[]>>(
        `${this.baseUrl}/filters/${entityType}/evaluate`,
        { filterDefinition },
        { observe: 'response', params }
      )
      .pipe(map(response => Number(response.headers.get('aw-totalitems') ?? 0)))
  }

  /**
   * Fetches filtered tasks for a draft filter and returns mapped Task instances.
   * @param {string} filterDraftId
   * @returns {Observable<Task[]>}
   */
  fetchTasksForDraftFilter(filterDraftId: string): Observable<Task[]> {
    const params = ApiClient.getQueryParams({ pageSize: this.filterResultsPageSize })

    return this.apiClient.getAll<Task[]>(`${this.baseUrl}/me/filters/drafts/${filterDraftId}/tasks`, { params }).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => Task.mapTask(entity))),
      tap(entities => this.taskStore.upsertMany(entities))
    )
  }

  /**
   * Evaluates a filter and returns mapped Project instances
   * @param {FilterDefinition} filterDefinition
   * @returns {Observable<Project[]>}
   */
  applyProjectFilter(filterDefinition: FilterDefinition): Observable<Project[]> {
    return this.apiClient.post<Project[]>(`${this.baseUrl}/filters/projects/evaluate`, { filterDefinition }).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => Project.mapProject(entity)))
    )
  }

  /**
   * Evaluates a filter and returns mapped User instances
   * @param {FilterDefinition} filterDefinition
   * @returns {Observable<User[]>}
   */
  applyUserFilter(filterDefinition: FilterDefinition): Observable<User[]> {
    return this.apiClient.post<User[]>(`${this.baseUrl}/filters/users/evaluate`, { filterDefinition }).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => new User(entity, 'filter')))
    )
  }

  /**
   * Evaluates a filter and returns mapped Company instances
   * @param {FilterDefinition} filterDefinition
   * @returns {Observable<Company[]>}
   */
  applyCompanyFilter(filterDefinition: FilterDefinition): Observable<Company[]> {
    return this.apiClient.post<Company[]>(`${this.baseUrl}/filters/companies/evaluate`, { filterDefinition }).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => new Company(entity)))
    )
  }

  /**
   * Fetch filtered tasks for a given filter
   * @param {string} filterId - The ID of the filter to apply
   * @returns {Observable<Task[]>}
   */
  fetchTasksForFilter(filterId: string): Observable<Task[]> {
    const params = ApiClient.getQueryParams({ pageSize: this.filterResultsPageSize })

    return this.apiClient.getAll<Task[]>(`${this.baseUrl}/me/filters/${filterId}/tasks`, { params }).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => Task.mapTask(entity))),
      tap(entities => this.taskStore.upsertMany(entities))
    )
  }

  /**
   * Fetch filtered projects for a given filter
   * @param {string} filterId - The ID of the filter to apply
   * @returns {Observable<Project[]>}
   */
  fetchFilteredProjects(filterId: string): Observable<Project[]> {
    return this.apiClient.get<Project[]>(`${this.baseUrl}/me/filters/${filterId}/projects`).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => Project.mapProject(entity))),
      tap(entities => this.projectStore.upsertMany(entities))
    )
  }

  /**
   * Fetch filtered users for a given filter
   * @param {string} filterId - The ID of the filter to apply
   * @returns {Observable<User[]>}
   */
  fetchFilteredUsers(filterId: string): Observable<User[]> {
    return this.apiClient.get<User[]>(`${this.baseUrl}/me/filters/${filterId}/users`).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => new User(entity, 'filter'))),
      tap(entities => this.userStore.upsertMany(entities))
    )
  }

  /**
   * Fetch filtered companies for a given filter
   * @param {string} filterId - The ID of the filter to apply
   * @returns {Observable<Company[]>}
   */
  fetchFilteredCompanies(filterId: string): Observable<Company[]> {
    return this.apiClient.get<Company[]>(`${this.baseUrl}/me/filters/${filterId}/companies`).pipe(
      map(entities => entities || []),
      map(entities => entities.map(entity => new Company(entity))),
      tap(entities => this.companyStore.upsertMany(entities))
    )
  }

  /**
   * Create a new filter
   * @param {FilterEntityType} entityType
   * @param {Partial<FilterEntity>} filter
   * @returns {Observable<FilterEntity>}
   */
  createFilter(entityType: FilterEntityType, filter: Partial<FilterEntity>): Observable<FilterEntity> {
    return this.apiClient.post<FilterEntity>(`${this.baseUrl}/me/filters/${entityType}`, filter).pipe(
      map(filter => new FilterEntity(filter)),
      tap(filter => this.entityFilterStore.add(filter)),
      tap(filter => this.completeTaskFilterOnboardingTask(filter)),
      tap(filter =>
        this.trackingService.trackEvent(TrackingEvent.filterCreated, {
          filter: filter.id,
          compact: filter.filterDefinition?.isCompact() ?? false,
          type: filter.entityType
        })
      )
    )
  }

  /**
   * Completes the onboarding task when a task filter is created.
   * @param {FilterEntity} filter
   */
  private completeTaskFilterOnboardingTask(filter: FilterEntity): void {
    if (filter.entityType !== FilterEntityType.Task) {
      return
    }

    this.onboardingService.updateTask(OnboardingTaskKey.taskOverviewAndDashboard, true).pipe(take(1)).subscribe()
  }

  /**
   * Generate or rewrite a filter from an AI prompt without persisting it.
   * @param {FilterEntityType} entityType
   * @param {string} prompt
   * @param {FilterDefinition} existingFilterDefinition
   * @returns {Observable<FilterEntity>}
   */
  createFilterFromPrompt(
    entityType: FilterEntityType,
    prompt: string,
    existingFilterDefinition?: FilterDefinition
  ): Observable<FilterEntity> {
    const body = existingFilterDefinition ? { prompt, existingFilterDefinition } : { prompt }

    return this.apiClient
      .post<FilterEntity>(`${this.baseUrl}/me/filters/${entityType}/fromprompt`, body)
      .pipe(map(filter => new FilterEntity(filter)))
  }

  /**
   * Fetches filter suggestions for a new or existing filter.
   * @param {FilterEntityType} entityType
   * @param {FilterDefinition} filterDefinition
   * @returns {Observable<FilterSuggestion[]>}
   */
  getFilterSuggestions(
    entityType: FilterEntityType,
    filterDefinition?: FilterDefinition
  ): Observable<FilterSuggestion[]> {
    const body = filterDefinition ? { filterDefinition } : {}

    return this.apiClient
      .post<FilterSuggestion[]>(`${this.baseUrl}/me/filters/${entityType}/suggestions`, body)
      .pipe(map(suggestions => suggestions.map(suggestion => new FilterSuggestion(suggestion))))
  }

  /**
   * Update an existing filter
   * @param {string} filterId
   * @param {Partial<FilterEntity>} filter
   * @returns {Observable<FilterEntity>}
   */
  updateFilter(filterId: string, filter: Partial<FilterEntity>): Observable<FilterEntity> {
    return this.apiClient.put<FilterEntity>(`${this.baseUrl}/me/filters/${filterId}`, filter).pipe(
      map(filter => new FilterEntity(filter)),
      tap(filter => this.entityFilterStore.upsert(filterId, filter))
    )
  }

  /**
   * Returns all filters and syncs them with the store
   * @param {FilterEntityType} entityType - Entity type to filter by
   * @param {boolean} includeShared - Optional flag to include shared filters
   * @param {QueryParams} params - Optional query parameters
   * @returns {Observable<FilterEntity[]>}
   */
  fetchAllFilters(
    entityType: FilterEntityType,
    includeShared?: boolean,
    params?: QueryParams
  ): Observable<FilterEntity[]> {
    const httpParams = ApiClient.getQueryParams(params)
    const routePrefix = includeShared ? `${this.baseUrl}/filters` : `${this.baseUrl}/me/filters`
    const route = entityType ? `${routePrefix}/${entityType}` : routePrefix

    return this.apiClient.get<FilterEntity[]>(route, { params: httpParams }).pipe(
      map(filters => filters.map(filter => new FilterEntity(filter))),
      tap(filters => {
        const storedFilters = this.entityFilterQuery.queryEntityFilters(entityType)
        this.entityFilterStore.sync(filters, storedFilters())
      })
    )
  }

  /**
   * Fetches the shared filters of the filter entity type
   * @param {FilterEntityType} entityType
   * @returns {Observable<FilterEntity[]>}
   */
  fetchSharedFilters(entityType: FilterEntityType): Observable<FilterEntity[]> {
    return this.apiClient.get<FilterEntity[]>(`${this.baseUrl}/me/sharedfilters/${entityType}`).pipe(
      map(filters => filters || []),
      map(filters => filters.map(filter => new FilterEntity(filter))),
      tap(filters => {
        const storedFilters = this.entityFilterQuery.queryEntityFilters(entityType, true)
        this.entityFilterStore.sync(filters, storedFilters())
      })
    )
  }

  /**
   * Get a filter
   * @param {string} filterId
   * @returns {Observable<FilterEntity>}
   */
  fetchFilter(filterId: string): Observable<FilterEntity> {
    return this.apiClient.get<FilterEntity>(`${this.baseUrl}/me/filters/${filterId}`).pipe(
      tap(filter => {
        if (!filter) {
          this.entityFilterStore.remove(filterId)

          return
        }
      }),
      map(filter => new FilterEntity(filter)),
      tap(filter => this.entityFilterStore.upsert(filterId, filter))
    )
  }

  /**
   * Copy a filter
   * @param {FilterEntity} filter - The filter to copy
   * @returns {Observable<FilterEntity>}
   */
  copyFilter(filter: FilterEntity): Observable<FilterEntity> {
    return this.apiClient.post<FilterEntity>(`${this.baseUrl}/me/filters/${filter.id}/copy`, {}).pipe(
      map(filter => new FilterEntity(filter)),
      tap(copiedFilter => this.entityFilterStore.add(copiedFilter))
    )
  }

  /**
   * Delete a filter
   * @param {string} filterId
   * @returns {Observable<void>}
   */
  deleteFilter(filterId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseUrl}/me/filters/${filterId}`).pipe(
      tap(() => this.entityFilterStore.remove(filterId)),
      tap(() => this.trackingService.trackEvent(TrackingEvent.filterDeleted, { filter: filterId }))
    )
  }

  /**
   * Subscribe to a shared filter
   * @deprecated TODO: Remove when granular sharing endpoints are live and the old feature flag is retired
   * @param {FilterEntity} filter
   * @returns {Observable<FilterEntity>}
   */
  subscribeToFilter(filter: FilterEntity): Observable<FilterEntity> {
    return this.apiClient.post<FilterEntity>(`${this.baseUrl}/me/filters/${filter.id}/subscribe`, {}).pipe(
      map(() => filter),
      tap(filter => this.entityFilterStore.upsert(filter.id, filter))
    )
  }

  /**
   * Unsubscribe from a shared filter
   * @deprecated TODO: Remove when granular sharing endpoints are live and the old feature flag is retired
   * @param {string} filterId
   * @returns {Observable<void>}
   */
  unsubscribeFromFilter(filterId: string): Observable<void> {
    return this.apiClient
      .post<void>(`${this.baseUrl}/me/filters/${filterId}/unsubscribe`, {})
      .pipe(tap(() => this.entityFilterStore.remove(filterId)))
  }

  // --- Filter Contributors ---

  /**
   * Adds contributors to a filter
   * @param {string} filterId - The filter to add contributors to
   * @param {{ userId: string; accessLevel: AccessLevels }[]} contributors - Contributors to add
   * @returns {Observable<FilterContributor[]>}
   */
  addFilterContributors(
    filterId: string,
    contributors: { userId: string; accessLevel: AccessLevels }[]
  ): Observable<FilterContributor[]> {
    return this.apiClient
      .post<FilterContributor[]>(`${this.baseUrl}/filters/${filterId}/contributors`, contributors)
      .pipe(
        map(result => result.map(c => new FilterContributor(c))),
        tap(newContributors =>
          this.entityFilterStore.update(filterId, filter => ({
            contributors: [...(filter.contributors || []), ...newContributors]
          }))
        ),
        tap(() =>
          this.trackingService.trackEvent(TrackingEvent.filterShared, {
            filter: filterId,
            scope: 'user'
          })
        )
      )
  }

  /**
   * Updates a contributor's access level on a filter
   * @param {string} filterId - The filter the contributor belongs to
   * @param {FilterContributor} contributor - The contributor to update
   * @returns {Observable<FilterContributor>}
   */
  updateFilterContributor(filterId: string, contributor: FilterContributor): Observable<FilterContributor> {
    return this.apiClient
      .put<FilterContributor>(`${this.baseUrl}/filters/${filterId}/contributors/${contributor.id}`, {
        accessLevel: contributor.accessLevel
      })
      .pipe(
        map(c => new FilterContributor(c)),
        tap(updated => {
          this.entityFilterStore.update(filterId, filter => ({
            contributors: (filter.contributors || []).map(c => (c.id === updated.id ? updated : c))
          }))
        })
      )
  }

  /**
   * Deletes a contributor from a filter
   * @param {string} filterId - The filter to remove the contributor from
   * @param {string} contributorId - The contributor to remove
   * @returns {Observable<void>}
   */
  deleteFilterContributor(filterId: string, contributorId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseUrl}/filters/${filterId}/contributors/${contributorId}`).pipe(
      tap(() => {
        this.entityFilterStore.update(filterId, filter => ({
          contributors: (filter.contributors || []).filter(c => c.id !== contributorId)
        }))
      })
    )
  }

  // --- Filter Teams ---

  /**
   * Adds team access entries to a filter
   * @param {string} filterId - The filter to add teams to
   * @param {{ teamId: string; accessLevel: AccessLevels }[]} teams - Teams to add
   * @returns {Observable<FilterTeamAccess[]>}
   */
  addFilterTeams(
    filterId: string,
    teams: { teamId: string; accessLevel: AccessLevels }[]
  ): Observable<FilterTeamAccess[]> {
    return this.apiClient.post<FilterTeamAccess[]>(`${this.baseUrl}/filters/${filterId}/teams`, teams).pipe(
      map(result => result.map(t => new FilterTeamAccess(t))),
      tap(newTeams =>
        this.entityFilterStore.update(filterId, filter => ({
          teams: [...(filter.teams || []), ...newTeams]
        }))
      ),
      tap(() =>
        this.trackingService.trackEvent(TrackingEvent.filterShared, {
          filter: filterId,
          scope: 'team'
        })
      )
    )
  }

  /**
   * Updates a team's access level on a filter
   * @param {string} filterId - The filter the team belongs to
   * @param {FilterTeamAccess} teamAccess - The team access to update
   * @returns {Observable<FilterTeamAccess>}
   */
  updateFilterTeam(filterId: string, teamAccess: FilterTeamAccess): Observable<FilterTeamAccess> {
    return this.apiClient
      .put<FilterTeamAccess>(`${this.baseUrl}/filters/${filterId}/teams/${teamAccess.id}`, {
        accessLevel: teamAccess.accessLevel
      })
      .pipe(
        map(t => new FilterTeamAccess(t)),
        tap(updated => {
          this.entityFilterStore.update(filterId, filter => ({
            teams: (filter.teams || []).map(t => (t.id === updated.id ? updated : t))
          }))
        })
      )
  }

  /**
   * Deletes a team access entry from a filter
   * @param {string} filterId - The filter to remove the team from
   * @param {string} filterTeamId - The team access entry to remove
   * @returns {Observable<void>}
   */
  deleteFilterTeam(filterId: string, filterTeamId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseUrl}/filters/${filterId}/teams/${filterTeamId}`).pipe(
      tap(() => {
        this.entityFilterStore.update(filterId, filter => ({
          teams: (filter.teams || []).filter(t => t.id !== filterTeamId)
        }))
      })
    )
  }

  /**
   * Returns all drafts for the authenticated user and entity type
   * @param {FilterEntityType} entityType
   * @returns {Observable<DraftFilterEntity[]>}
   */
  fetchDrafts(entityType: FilterEntityType): Observable<DraftFilterEntity[]> {
    return this.apiClient.get<DraftFilterEntity[]>(`${this.baseUrl}/me/filters/${entityType}/drafts`).pipe(
      map(drafts => drafts.map(draft => new DraftFilterEntity(draft))),
      tap(drafts => {
        this.draftFilterStore.setDraftsForEntityType(entityType, drafts)
      })
    )
  }

  /**
   * Creates a new draft filter
   * @param {FilterEntityType} entityType
   * @param {Partial<DraftFilterEntity>} draft
   * @returns {Observable<DraftFilterEntity>}
   */
  createDraft(entityType: FilterEntityType, draft: Partial<DraftFilterEntity>): Observable<DraftFilterEntity> {
    return this.apiClient.post<DraftFilterEntity>(`${this.baseUrl}/me/filters/${entityType}/drafts`, draft).pipe(
      map(draftResponse => new DraftFilterEntity(draftResponse)),
      tap(createdDraft => this.draftFilterStore.addDraft(createdDraft)),
      tap(createdDraft =>
        this.trackingService.trackEvent(TrackingEvent.draftFilterCreated, {
          filter: createdDraft.filterId ?? createdDraft.id,
          type: createdDraft.entityType,
          conditions: this.getConditionCount(createdDraft.filterDefinition)
        })
      )
    )
  }

  /**
   * Updates an existing draft filter
   * @param {string} filterDraftId
   * @param {Partial<DraftFilterEntity>} draft
   * @returns {Observable<DraftFilterEntity>}
   */
  updateDraft(filterDraftId: string, draft: Partial<DraftFilterEntity>): Observable<DraftFilterEntity> {
    return this.apiClient.put<DraftFilterEntity>(`${this.baseUrl}/me/filters/drafts/${filterDraftId}`, draft).pipe(
      map(updated => new DraftFilterEntity(updated)),
      tap(updatedDraft => this.draftFilterStore.upsertDraft(filterDraftId, updatedDraft)),
      tap(updatedDraft => this.draftUpdatedSubject.next(updatedDraft))
    )
  }

  /**
   * Deletes a draft filter
   * @param {string} filterDraftId
   * @returns {Observable<void>}
   */
  deleteDraft(filterDraftId: string): Observable<void> {
    return this.apiClient
      .delete<void>(`${this.baseUrl}/me/filters/drafts/${filterDraftId}`)
      .pipe(tap(() => this.draftFilterStore.removeDraft(filterDraftId)))
  }

  /**
   * Returns the total number of conditions across all condition groups.
   * @param {FilterDefinition | undefined} filterDefinition
   * @returns {number}
   */
  private getConditionCount(filterDefinition: FilterDefinition | undefined): number {
    if (!filterDefinition?.conditionGroups) {
      return 0
    }

    return filterDefinition.conditionGroups.reduce((total, group) => total + (group.conditions?.length ?? 0), 0)
  }
}
